import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextInput from 'react/elements/TextInput';
import MaterialIcon from 'react/elements/MaterialIcon';
import InlineCircleLoader from 'react/components/InlineCircleLoader';
import {
	fetchMealIdeaRecipe,
	updateMealIdea,
	MEAL_IDEAS_FAIL,
	MEAL_IDEAS_LOADED,
} from 'redux/actions/mealIdeas';
import useOffline, { NEEDS_CONNECTION_TITLE, onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

/** Only a web address becomes a link or an image; anything else is shown as text. */
export function isWebAddress(value) {
	return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

/**
 * The recipe link, shown as a card with the title and image the server read from the page. Saving
 * a new address asks the server to read it (task 5's endpoint); that can take several seconds on a
 * slow site, so the refresh icon becomes a spinner meanwhile. Both are online-only, so offline the
 * link is read-only and the page cannot be read again; a link left offline saves once back online.
 */
export default function RecipeCard({ idea }) {
	const dispatch = useDispatch();
	const [url, setUrl] = useState(idea.recipe_url || '');
	const [fetching, setFetching] = useState(false);
	const [fetchFailure, setFetchFailure] = useState(null);
	const mounted = useRef(true);
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);

	useEffect(() => () => {
		mounted.current = false;
	}, []);

	const fetchRecipe = () => {
		setFetching(true);
		setFetchFailure(null);
		return dispatch(fetchMealIdeaRecipe(idea.id)).then((action) => {
			if(!mounted.current) {
				return;
			}
			setFetching(false);
			if(action && action.type === MEAL_IDEAS_FAIL) {
				setFetchFailure(action.payload);
			}
		});
	};

	const saveUrl = () => {
		const trimmed = url.trim();
		if(trimmed === (idea.recipe_url || '')) {
			return;
		}
		dispatch(updateMealIdea(idea.id, { recipe_url: trimmed })).then((action) => {
			if(mounted.current && trimmed && action && action.type === MEAL_IDEAS_LOADED) {
				fetchRecipe();
			}
		});
	};

	const savedUrl = idea.recipe_url || '';
	const error = fetchFailure || idea.recipe_fetch_error;
	const image = isWebAddress(idea.recipe_image_url) ? idea.recipe_image_url : null;
	const label = idea.recipe_title || savedUrl;

	let refresh = <MaterialIcon className='clickable' title='Read the recipe page again' onClick={fetchRecipe}>refresh</MaterialIcon>;
	if(fetching) {
		refresh = <InlineCircleLoader size='small' />;
	} else if(offline) {
		refresh = <MaterialIcon className='is-disabled' title={NEEDS_CONNECTION_TITLE} aria-disabled='true'>refresh</MaterialIcon>;
	}

	const link = isWebAddress(savedUrl) ?
		<a href={savedUrl} target='_blank' rel='noopener noreferrer'>{label}</a> :
		<span>{label}</span>;

	return (
		<div className='meal_idea_recipe'>
			<div className='input-field'>
				<TextInput
					aria-label='Recipe link'
					placeholder='Paste a recipe link'
					{...onlineOnlyField(offline)}
					type='url'
					value={url}
					onChange={(event) => setUrl(event.target.value)}
					onBlur={() => whenOnline('url', saveUrl)}
					onKeyDown={(event) => {
						if(event.key === 'Enter') {
							event.preventDefault();
							whenOnline('url', saveUrl);
						}
					}}
				/>
			</div>
			{!!savedUrl &&
			<div className={`recipe_card ${image ? 'has-image' : ''}`}>
				{image && <img className='recipe_card__image' src={image} alt='' />}
				<div className='recipe_title'>
					{link}
					{refresh}
				</div>
				{!!error && <div className='recipe_fetch_error'>{error}</div>}
			</div>
			}
		</div>
	);
}
